
import { useState, useRef, useCallback, useEffect } from 'react';

const RESEND_SECONDS = 45;

interface CartOtpStepProps {
  onVerified: (otpCode: string) => void;
  onBack: () => void;
  onResend: () => Promise<void>;
  submitting: boolean;
  externalError: string | null;
  initialCountdown?: number | null;
}

export default function CartOtpStep({ onVerified, onBack, onResend, submitting, externalError, initialCountdown }: CartOtpStepProps) {
  const startSeconds = initialCountdown != null && initialCountdown > 0 ? initialCountdown : RESEND_SECONDS;
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(startSeconds);
  const [canResend, setCanResend] = useState(startSeconds <= 0);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Lock síncrono contra doble click: el `submitting` prop viene como state
  // de React (async). Sin ref el handler dispara dos POST antes que el render
  // baje el botón a disabled.
  const submitLockRef = useRef(false);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      const char = value.replace(/\D/g, '').slice(-1);
      const next = [...digits];
      next[index] = char;
      setDigits(next);
      setError('');
      if (char && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      if (!pasted) return;
      const next = [...digits];
      for (let i = 0; i < pasted.length; i++) {
        next[i] = pasted[i];
      }
      setDigits(next);
      const focusIndex = Math.min(pasted.length, 5);
      inputRefs.current[focusIndex]?.focus();
    },
    [digits]
  );

  const handleSubmit = () => {
    if (submitLockRef.current) return; // ya disparado, ignora click duplicado
    const code = digits.join('');
    if (code.length < 6) {
      setError('Ingresa los 6 dígitos del código');
      return;
    }
    submitLockRef.current = true;
    onVerified(code);
  };

  // Si la verificación falla y vuelve un error, libera el lock para permitir reintentar
  useEffect(() => {
    if (externalError) submitLockRef.current = false;
  }, [externalError]);

  const handleResend = async () => {
    if (!canResend || resending) return;
    setResending(true);
    try {
      await onResend();
      setCanResend(false);
      setCountdown(RESEND_SECONDS);
      setDigits(Array(6).fill(''));
      setError('');
      inputRefs.current[0]?.focus();
    } catch {
      setError('Error al reenviar el código');
    } finally {
      setResending(false);
    }
  };

  const formatCountdown = (s: number) => {
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const code = digits.join('');
  const isComplete = code.length === 6;
  const displayError = error || externalError;

  return (
    <div className="flex flex-col">
      <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 bg-slate-50/50 dark:bg-slate-800/20">
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
        </button>
        <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">verified_user</span>
          Verificación OTP
        </h3>
      </div>

      <div className="p-5 space-y-5">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-3">
            <span className="material-symbols-outlined text-2xl text-primary">security</span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Ingresa el código de verificación enviado a tu correo para confirmar la solicitud.
          </p>
        </div>

        <div className="flex gap-2 justify-center">
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              disabled={submitting}
              className="w-11 h-12 text-center text-lg font-bold border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-slate-800 dark:text-white shadow-sm transition-colors disabled:opacity-50"
            />
          ))}
        </div>

        {displayError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-sm">error</span>
            {displayError}
          </div>
        )}

        <div className="text-center">
          <p className="text-xs text-slate-400 mb-1">
            ¿No recibiste el código?
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend || resending}
            className="text-xs font-medium text-primary hover:text-blue-600 transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            {resending ? 'Reenviando...' : 'Reenviar código'}
          </button>
          {!canResend && (
            <p className="text-[10px] text-slate-400 mt-1">
              Puedes solicitar uno nuevo en {formatCountdown(countdown)}
            </p>
          )}
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-800/50 p-5 border-t border-slate-100 dark:border-slate-800">
        <button
          onClick={handleSubmit}
          disabled={!isComplete || submitting}
          className="w-full bg-primary hover:bg-blue-700 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-primary/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              Solicitando...
            </>
          ) : (
            <>
              Solicitar Adelanto
              <span className="material-symbols-outlined text-sm">check_circle</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
